import * as React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useCallback, useEffect, useState } from 'react';
import { IWaypoint } from '../Waypoints/types';
import { IVehiclePosition, IVehicleWithData, IVehiclesWithData } from '../../Vehicles/types';
import { BluecoinData } from '../Bluecoins/types';
import { selectedVehiclesWithDataSelector } from '../../../store/recoil/vehicles';
import { rideAutofocusAtom } from '../../RidePlayer/RidePlayerState';
import { isOnlinePanelOpenAtom } from '../../OnlinePanel/OnlinePanelState';
import {
  bluecoinIdAtom,
  bluecoinsDataSelector,
  isBluecoinsOpenAtom,
} from '../Bluecoins/BluecoinState';
import {
  isDrawWaypointAtom,
  isEditModeAtom,
  isWaypointsOpenAtom,
  waypointIdAtom,
  waypointsDataAtom,
} from '../Waypoints/WaypointState';
import {
  autofocusAtom,
  filteredVehiclesAtom,
  filterStringAtom,
  isRidePlayerOpenAtom,
  vehicleFocusAtom,
} from '../OnlineMapState';

interface IBoundsProps {
  googleMap: google.maps.Map | undefined;
}

function Bounds(props: IBoundsProps) {
  const [rideAutoFocus, setRideAutofocus] = useRecoilState<boolean>(rideAutofocusAtom);
  const [autofocus, setAutofocus] = useRecoilState<boolean>(autofocusAtom);
  const selectedVehiclesWithData = useRecoilValue<IVehiclesWithData>(
    selectedVehiclesWithDataSelector
  );
  const isOnlinePanelOpen = useRecoilValue<number | false>(isOnlinePanelOpenAtom);
  const filterString = useRecoilValue<string>(filterStringAtom);
  const [vehicleFocus, setVehicleFocus] = useRecoilState<boolean>(vehicleFocusAtom);
  const isWaypointsOpen = useRecoilValue<boolean>(isWaypointsOpenAtom);
  const isRidePlayerOpen = useRecoilValue<number | boolean>(isRidePlayerOpenAtom);
  const filteredVehicles = useRecoilValue<number[]>(filteredVehiclesAtom);
  const [initializedBounds, setInitializedBounds] = useState<boolean>(false);
  const bluecoinData = useRecoilValue<BluecoinData[]>(bluecoinsDataSelector);
  const isBluecoinsOpen = useRecoilValue<boolean>(isBluecoinsOpenAtom);
  const selectedBluecoinId = useRecoilValue<number | null>(bluecoinIdAtom);
  const waypointsData = useRecoilValue<IWaypoint[]>(waypointsDataAtom);
  const waypointId = useRecoilValue<number | null>(waypointIdAtom);
  const isEditMode = useRecoilValue<boolean>(isEditModeAtom);
  const isDrawWaypoint = useRecoilValue<boolean>(isDrawWaypointAtom);

  const handleMouseEventListener = useCallback(() => {
    setVehicleFocus(false);
    setAutofocus(false);
    setRideAutofocus(false);
  }, [setAutofocus, setRideAutofocus, setVehicleFocus]);

  const handleKeyPressListener = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === '+' || event.key === '-') {
        setVehicleFocus(false);
        setAutofocus(false);
        setRideAutofocus(false);
      }
    },
    [setAutofocus, setRideAutofocus, setVehicleFocus]
  );

  const handleDragEvent = useCallback(() => {
    setVehicleFocus(false);
    setAutofocus(false);
    setRideAutofocus(false);
  }, [setAutofocus, setRideAutofocus, setVehicleFocus]);

  const handleBoundsEvent = useCallback(
    (googleMapBounds: google.maps.Map) => {
      const mapBounds = googleMapBounds.getBounds();
      if (mapBounds && (autofocus || rideAutoFocus || vehicleFocus)) {
        googleMapBounds.setCenter(mapBounds.getCenter());
      }
    },
    [autofocus, rideAutoFocus, vehicleFocus]
  );

  useEffect(() => {
    const element = document.getElementById('google-map-fullscreen');
    if (initializedBounds && props.googleMap) {
      google.maps.event.addListenerOnce(props.googleMap, 'dragstart', handleDragEvent);
      google.maps.event.addListenerOnce(props.googleMap, 'bounds_changed', () => {
        if (props.googleMap) {
          handleBoundsEvent(props.googleMap);
        }
      });

      if (element) {
        element.addEventListener('wheel', handleMouseEventListener, { passive: true });
        element.addEventListener('dblclick', handleMouseEventListener);
        element.addEventListener('keydown', handleKeyPressListener);
        element.addEventListener('keypress', handleKeyPressListener);
        element.addEventListener('keyup', handleKeyPressListener);
      }
    }
    setInitializedBounds(true);
    return () => {
      if (initializedBounds && props.googleMap) {
        // const element = document.getElementById('google-map-fullscreen');
        if (element) {
          element.removeEventListener('wheel', handleMouseEventListener);
          element.removeEventListener('dblclick', handleMouseEventListener);
          element.removeEventListener('keydown', handleKeyPressListener);
          element.removeEventListener('keypress', handleKeyPressListener);
          element.removeEventListener('keyup', handleKeyPressListener);
        }
      }
    };
  }, [
    handleBoundsEvent,
    handleDragEvent,
    handleKeyPressListener,
    handleMouseEventListener,
    initializedBounds,
    props.googleMap,
  ]);

  // Handle bounds
  useEffect(() => {
    if (props.googleMap) {
      if (
        autofocus &&
        Object.keys(selectedVehiclesWithData).length > 1 &&
        filterString.length === 0 &&
        !vehicleFocus &&
        !isWaypointsOpen &&
        !isBluecoinsOpen
      ) {
        const mapBounds = new google.maps.LatLngBounds();
        if (Object.keys(selectedVehiclesWithData).length > 1) {
          Object.keys(selectedVehiclesWithData).forEach((vehicleId: string) => {
            if (
              selectedVehiclesWithData[vehicleId] &&
              (selectedVehiclesWithData[vehicleId] as IVehicleWithData).position
            ) {
              const vehiclePosition: IVehiclePosition | null = (
                selectedVehiclesWithData[vehicleId] as IVehicleWithData
              ).position;
              if (vehiclePosition) {
                mapBounds.extend(
                  new google.maps.LatLng(vehiclePosition.latitude, vehiclePosition.longitude)
                );
              }
            }
          });

          props.googleMap.fitBounds(mapBounds, {
            top: isOnlinePanelOpen ? 300 : 100,
            bottom: 100,
            left: 100,
            right: 100,
          });
        }
      }

      if (
        isWaypointsOpen &&
        waypointsData.length > 0 &&
        autofocus &&
        !waypointId &&
        !isDrawWaypoint &&
        !isEditMode
      ) {
        const mapBounds = new google.maps.LatLngBounds();
        waypointsData.forEach((waypoint) => {
          if (
            typeof waypoint.center?.latitude === 'number' &&
            typeof waypoint.center.longitude === 'number'
          ) {
            const position = new google.maps.LatLng(
              waypoint.center.latitude,
              waypoint.center.longitude
            );
            mapBounds.extend(position);
          }
        });

        props.googleMap.fitBounds(mapBounds, { top: 50, right: 50, bottom: 100, left: 300 });
      }

      if (isBluecoinsOpen && bluecoinData.length > 0 && autofocus) {
        const mapBounds = new google.maps.LatLngBounds();
        bluecoinData.forEach((bluecoin) => {
          if (
            typeof bluecoin.position.latitude === 'number' &&
            typeof bluecoin.position.longitude === 'number'
          ) {
            const position = new google.maps.LatLng(
              bluecoin.position.latitude,
              bluecoin.position.longitude
            );
            mapBounds.extend(position);
          }
        });
        if (typeof selectedBluecoinId === 'number') {
          props.googleMap.fitBounds(mapBounds, { top: 250, right: 50, bottom: 100, left: 300 });
        } else {
          props.googleMap.fitBounds(mapBounds, { top: 50, right: 50, bottom: 100, left: 300 });
        }
      }

      if (
        autofocus &&
        filteredVehicles.length > 1 &&
        !vehicleFocus &&
        !isWaypointsOpen &&
        !isBluecoinsOpen
      ) {
        const mapBounds = new google.maps.LatLngBounds();
        filteredVehicles.forEach((vehicleId: number) => {
          const vehicle = selectedVehiclesWithData[vehicleId];
          if (vehicle && vehicle.position) {
            const position = new google.maps.LatLng(
              vehicle.position.latitude,
              vehicle.position.longitude
            );
            mapBounds.extend(position);
          }
        });
        props.googleMap.fitBounds(mapBounds, {
          top: typeof isOnlinePanelOpen === 'number' ? 300 : 100,
          bottom: 100,
          left: 100,
          right: 100,
        });
      }

      if (
        autofocus &&
        Object.keys(selectedVehiclesWithData).length === 1 &&
        filteredVehicles.length === 0 &&
        !vehicleFocus &&
        !isWaypointsOpen &&
        !isBluecoinsOpen
      ) {
        const vehicle: IVehicleWithData =
          selectedVehiclesWithData[Object.keys(selectedVehiclesWithData)[0]];
        if (vehicle && vehicle.position) {
          const position = new google.maps.LatLng(
            vehicle.position.latitude,
            vehicle.position.longitude
          );
          props.googleMap.setCenter(position);
          props.googleMap.setZoom(15);
        }
      }

      if (
        autofocus &&
        filteredVehicles.length === 1 &&
        !vehicleFocus &&
        !isWaypointsOpen &&
        !isBluecoinsOpen
      ) {
        // v riadku nizsie je potrebne vyberat data cez filteredVehicles, pretoze tu potrebujeme to, co je vyfiltrovane - jedine vozidlo
        const vehicle: IVehicleWithData = selectedVehiclesWithData[filteredVehicles[0]];
        if (vehicle && vehicle.position) {
          const position = new google.maps.LatLng(
            vehicle.position.latitude,
            vehicle.position.longitude
          );
          props.googleMap.setCenter(position);
          props.googleMap.setZoom(15);
        }
      }

      if (vehicleFocus && isOnlinePanelOpen && !isWaypointsOpen && !isBluecoinsOpen) {
        const vehicle: IVehicleWithData = selectedVehiclesWithData[
          isOnlinePanelOpen
        ] as IVehicleWithData;
        if (vehicle && vehicle.position) {
          const position = new google.maps.LatLng(
            vehicle.position.latitude,
            vehicle.position.longitude
          );
          props.googleMap.setCenter(position);
          props.googleMap.setZoom(15);
        }
      }
    }

    // Disable autofocus, when ride player is open
    if (isRidePlayerOpen) {
      setAutofocus(false);
    }
  });

  return null;
}

export default Bounds;
